import type { FlagDefinition } from './types.js';

/**
 * The flag registry — the authoritative list of feature flags (ADR-0022).
 *
 * Adding a flag means adding a definition here; the DB needs no row until an
 * operator sets a non-default value. Removing a flag from this list makes any
 * surviving DB rows inert (evaluation only considers registered keys), so
 * retire flags by deleting the definition and archiving the row.
 *
 * Each defaultValue is the flag's DECLARED SAFE VALUE — the behaviour a
 * consumer must fall back to when it can't reach the DB or receives no flag
 * map. For the current gates that is the dark/off direction, but "off" is not
 * universally safe; argue the safe direction in PR review per ADR-0022.
 */
export const FLAG_REGISTRY: readonly FlagDefinition[] = [
  {
    key: 'auto-pause',
    description:
      'Auto-pause agents on sustained hourly-cost breach (ENG-5561). Boolean gate; ships dark.',
    flagType: 'boolean',
    defaultValue: false,
    envVar: 'AUGMENTED_AUTO_PAUSE_ENABLED',
    // Cost-enforcement gate: relaxing it (turning auto-pause off) removes a
    // spend control, so mutations require explicit confirmation.
    sensitive: true,
  },
  {
    key: 'team-scoped-visibility',
    description:
      'Restrict team-scoped reads to direct team members (ENG-7122). By default the API ' +
      'resolves X-Team-Slug for any org member (org-membership fallback), so a plain org ' +
      'member can read a sibling team they are not on (agents, kanban, run-derived spend) ' +
      'because the service-role API filters only by the org-resolved team_id. When ON for ' +
      'an org, the org-membership fallback is dropped for plain org member/viewer (they may ' +
      'only resolve teams they are a direct team_members of), while org owner/admin keep ' +
      'cross-team reach and the admin-onboarding bypass is preserved. The GET /teams ' +
      'listing is narrowed the same way. Boolean gate; default off (current behaviour).',
    flagType: 'boolean',
    defaultValue: false,
    // Tightening control: turning this flag OFF *relaxes* a visibility restriction,
    // so mutations require explicit confirmation per ADR-0022 §4.
    sensitive: true,
  },
  {
    key: 'channel-busy-ack',
    description:
      'Busy-but-alive ack notices when an agent is mid-task (ENG-6180). Boolean gate; ships dark.',
    flagType: 'boolean',
    defaultValue: false,
    envVar: 'AGT_CHANNEL_BUSY_ACK_ENABLED',
  },
  {
    key: 'live-page-comments',
    description:
      'Text-selection commenting on public Augmented Live pages for authenticated team members ' +
      '(ENG-6788). When on, a member viewing live.augmented.team/{slug} can select text and send ' +
      'a comment to the agent via the relay bridge; evaluated per-org in GET /artifacts/:slug/' +
      'comment-access. Additive feature, not an enforcement control. Boolean gate; ships dark.',
    flagType: 'boolean',
    defaultValue: false,
  },
  {
    key: 'augmented-live-stream-producer',
    description:
      'Augmented Live live-preview streaming producer (ENG-7210). The receiver (codec, ' +
      'artifact-draft channel, stream route, console Live Preview tab) shipped under ENG-6234 ' +
      'but never engages because nothing writes the working file the manager scanner watches. ' +
      'When ON for an org, a host-side PostToolUse hook mirrors agt-live.publish/editing content ' +
      'to ~/.augmented/{codeName}/artifacts/<slug>/index.html, so the scanner mints a draft and ' +
      'streams a keyframe to the console Live Preview tab. Additive, best-effort. Boolean gate; ' +
      'ships dark. Materialized to the host flags-cache; the bash hook reads it (operator/canary ' +
      'override AGT_LIVE_STREAM_PRODUCER_ENABLED).',
    flagType: 'boolean',
    defaultValue: false,
    envVar: 'AGT_LIVE_STREAM_PRODUCER_ENABLED',
  },
  {
    key: 'admin-live-pane',
    description:
      'Live agent pane streaming on the platform-admin surfaces (ENG-6588): the agent ' +
      'Diagnostics tab and the /admin embed poll GET /admin/agents/:id/pane (tmux ' +
      'capture-pane over SSM) ~every 3s for a bounded window. Boolean gate; ships dark; ' +
      'when OFF the route returns feature_disabled and the UI falls back to the 30s static ' +
      'diagnostics snapshot. It is the no-deploy kill switch bounding the shared-SSM blast ' +
      'radius (SSM SendCommand throttles account-wide, shared with incident-response runbooks).',
    flagType: 'boolean',
    defaultValue: false,
    // No envVar: the DB stage/org override is the kill switch (ADR-0022), so there
    // is no host-side env override to materialise.
    //
    // Exposed to the browser so the admin UI can hide the live control when the
    // gate is off rather than rendering a button that 403s.
    public: true,
  },
  {
    key: 'admin-send-keys',
    description:
      'Interactive send-keys rescue on the live terminal (ENG-6611): POST ' +
      '/admin/agents/:id/send-keys runs a buttons-only, allowlisted tmux send-keys (Enter, ' +
      'Ctrl-C, Esc, arrows, single-char answer + Enter) over SSM to rescue a wedged agent. ' +
      'It is a customer-host WRITE, so it ships dark and the route ALSO hard-gates the target ' +
      "to an is_internal (IL-owned) org regardless of this flag - customer agents are a " +
      'separate later decision. Gate evaluated server-side at the send endpoint, independent ' +
      'of Diagnostics-tab visibility (the ENG-6639 firewall). No free-text (that is ENG-6643).',
    flagType: 'boolean',
    defaultValue: false,
    // Capability change on customer infrastructure; flipping it is worth an explicit confirm.
    sensitive: true,
    // Exposed to the browser so the live modal can show/hide the quick-action row.
    public: true,
  },
  {
    key: 'channel-replay',
    description:
      'Durable channel-inbound replay (ENG-5969): re-push an uncleared pending-inbound ' +
      'marker when the session is alive so a dropped fire-once notification is recovered ' +
      '(bounded by MAX_MARKER_REPLAYS). Now the fleet default (enabled fleet-wide by ' +
      'ENG-6354, promoted to the compiled default by ENG-6683). The flag and the ' +
      'AGT_CHANNEL_REPLAY_ENABLED env override are retained as the operational kill ' +
      'switch (it actively re-delivers inbound); precedence is env override > flag ' +
      'value > this default.',
    flagType: 'boolean',
    defaultValue: true,
    envVar: 'AGT_CHANNEL_REPLAY_ENABLED',
  },
  {
    key: 'restart-doorbell',
    description:
      'Fast agent-restart lane (ENG-7335): when ON, the manager subscribes to ' +
      'host_agents restart_requested_at changes over its existing Realtime channel ' +
      'and services a dashboard-issued restart on a narrow per-agent lane (kill + ' +
      'respawn the one agent) within a few seconds, instead of waiting up to 50-110s ' +
      'for the next full poll cycle to notice it. The slow poll remains the durable ' +
      'backstop, so a missed doorbell only costs latency, never correctness. Ships ' +
      'dark (default off); staged on the agt-aws-1 canary before fleet enable. The ' +
      'AGT_RESTART_DOORBELL_ENABLED env override is the per-host escape hatch and ' +
      'kill switch; precedence is env override > flag value > this default.',
    flagType: 'boolean',
    defaultValue: false,
    envVar: 'AGT_RESTART_DOORBELL_ENABLED',
  },
  {
    key: 'fast-mcp-restart-respawn',
    description:
      'Fast MCP-restart respawn (ENG-7401 Phase 2): when ON, after the manager stops ' +
      'an agent session for an MCP-servers change (integration add/remove, channel-set ' +
      'change, managed-toolkit churn, bind remediation) it respawns that one agent ' +
      'immediately - reusing the same spawn path as the poll and the restart-doorbell ' +
      'fast lane - instead of waiting up to a full poll cycle (~50-110s) for the next ' +
      'health check to notice the session is down. .mcp.json was already re-rendered ' +
      'before the stop, so the immediate respawn picks up the new config. The slow ' +
      'poll remains the durable backstop, so a failed fast respawn only costs latency, ' +
      'never correctness. Ships dark (default off); staged on the agt-aws-1 canary ' +
      'before fleet enable. The AGT_FAST_MCP_RESTART_RESPAWN_ENABLED env override is ' +
      'the per-host escape hatch and kill switch; precedence is env override > flag ' +
      'value > this default.',
    flagType: 'boolean',
    defaultValue: false,
    envVar: 'AGT_FAST_MCP_RESTART_RESPAWN_ENABLED',
  },
  {
    key: 'channel-silent-loss-alarm',
    description:
      'Channel silent-loss alarm (ENG-6728): when ON, the responsiveness-probe route ' +
      'creates a per-agent CloudWatch alarm that pages on a `ChannelDeflections` ' +
      'Cause=replay_orphaned datapoint (a recoverable inbound aged out without ' +
      'delivery). Observe-first rollout gate, flipped stage-wide from the admin ' +
      'Feature Flags page; the underlying metric ships since ENG-6355. The ' +
      'AUGMENTED_CHANNEL_SILENT_LOSS_ALARM_ENABLED env var is retained as the ' +
      'highest-precedence operator override; precedence is env override > flag value > ' +
      'this default.',
    flagType: 'boolean',
    defaultValue: false,
    envVar: 'AUGMENTED_CHANNEL_SILENT_LOSS_ALARM_ENABLED',
    // Enabling the alarm pages on customer-facing message loss; flipping it is an
    // operational decision with blast radius, so mutations require confirmation.
    sensitive: true,
  },
  {
    key: 'connectivity-probe',
    description:
      'Host-side rolling integration connectivity probe (ENG-5641): when ON, the ' +
      'manager probes each integration ~hourly using the agent\'s real creds/MCP and ' +
      'reports to /host/integration-connectivity, which populates last_connectivity_* ' +
      'and feeds the central escalation evaluator (integration-connectivity-escalation) ' +
      'and reconnect notifications. It is the only continuous writer of ' +
      'last_connectivity_status (the monitor cron only reads it). Migrated from the raw ' +
      'AGT_CONNECTIVITY_PROBE_ENABLED env gate to this registry flag (ENG-7220) so both ' +
      'halves of the connectivity subsystem are flag-managed; the env var is retained as ' +
      'the highest-precedence operator override (env override > flag value > this ' +
      'default). Boolean gate; ships dark.',
    flagType: 'boolean',
    defaultValue: false,
    envVar: 'AGT_CONNECTIVITY_PROBE_ENABLED',
  },
  {
    key: 'session-tool-probe',
    description:
      'Host-side live-session tool-bind probe (ENG-7053 / ENG-7220): when ON, the ' +
      'manager periodically checks whether each integration\'s MCP tools are actually ' +
      'bound in the running agent session (stdio child alive / remote MCP answers ' +
      'tools/list) and reports a bound|missing|unreachable|unknown verdict to ' +
      '/host/session-tool-bind. This is the signal the Add Integration modal polls to ' +
      'confirm a newly-added integration came online after the agent\'s next restart, ' +
      'rather than resolving at "credentials verified". Observational only (no auto ' +
      'rebind). Boolean gate; ships dark. AGT_SESSION_TOOL_PROBE_ENABLED is the ' +
      'highest-precedence operator override; precedence is env override > flag value > ' +
      'this default.',
    flagType: 'boolean',
    defaultValue: false,
    envVar: 'AGT_SESSION_TOOL_PROBE_ENABLED',
  },
  {
    key: 'session-tool-rebind',
    description:
      'Auto-rebind MCP tools that fell out of a running session (ENG-7318, ' +
      'ENG-7053 Slice A). When ON, the session-tool-bind probe does not just ' +
      'report a hard-negative verdict - the manager SIGTERMs just that ' +
      "integration's MCP child so Claude Code's transport respawns it (no full " +
      'agent restart, conversation context preserved). Fires on `missing` (a dead ' +
      'stdio/proxy child - the ENG-7301/Phil case) and on `unreachable` (an alive ' +
      'ENG-6859 OAuth-proxy child whose upstream stopped answering tools/list after ' +
      'a token rotated under the live session - the ENG-7318/Pepper case). Requires ' +
      'the `session-tool-probe` flag (it consumes that probe\'s verdict). The reap ' +
      "is debounced (the probe's ~1h cadence plus a per-integration cooldown) so a " +
      'persistently-broken integration is rebound at most once per window, never ' +
      'every cycle. Boolean gate; ships dark - canary per host (agt-aws-1) before ' +
      'any fleet flip. AGT_SESSION_TOOL_REBIND_ENABLED is the highest-precedence ' +
      'operator override; precedence is env override > flag value > this default.',
    flagType: 'boolean',
    defaultValue: false,
    envVar: 'AGT_SESSION_TOOL_REBIND_ENABLED',
    // Reaps MCP children on a live customer agent (targeted, not a full restart,
    // but still a runtime mutation on customer infra); flipping it is worth an
    // explicit confirm.
    sensitive: true,
  },
  {
    key: 'channel-skip-reaction',
    description:
      'Seen-but-skipping reaction (ENG-6464): the agent adds a configured emoji ' +
      '(e.g. ➖ / 🫡) to a DM or thread message it saw but deliberately chose not ' +
      'to reply to, so the sender can tell "seen and skipped" from "never ' +
      'received". Explicit-agent-verb model (no Stop-hook inference). Boolean ' +
      'gate; ships dark — inert until flipped on per host/org.',
    flagType: 'boolean',
    defaultValue: false,
    envVar: 'AGT_CHANNEL_SKIP_REACTION_ENABLED',
  },
  {
    key: 'channel-block-turn-end',
    description:
      'Block-turn-end / composed-but-unsent detector (ENG-6467, ADR-0024 Slice 2.5): ' +
      'closes the D1 silent-loss class where the agent composes a channel reply as ' +
      'plain turn text and ends the turn without calling the reply tool. When ON, the ' +
      'ghost-reply Stop hook returns {"decision":"block"} for an inbound it owes a reply ' +
      'to (a non-discretionary, non-undeliverable pending marker for the last channel ' +
      'tag, with no matching reply tool_use this turn) so the MODEL sends the reply itself ' +
      '— right thread, right content, no recovery mis-correlation (D2). Capped to one ' +
      'block per inbound via a per-marker ledger (NOT stop_hook_active, which is ' +
      'unverified across --resume); after one block it falls through to the existing ' +
      'recovery-outbox. Boolean gate; ships dark — canary per host (agt-aws-1) before any ' +
      'fleet flip. When OFF the hook behaves exactly as today (recovery only).',
    flagType: 'boolean',
    defaultValue: false,
    envVar: 'AGT_CHANNEL_BLOCK_TURN_END_ENABLED',
    // Gating turn-completion fleet-wide is a high-blast-radius capability change;
    // flipping it (esp. beyond the canary host) is worth an explicit confirm.
    sensitive: true,
  },
  {
    key: 'block-turn-end-all-markers',
    description:
      'Multi-marker block-turn-end (ENG-7397 / WS3, cross-thread reply routing). ' +
      'Extends channel-block-turn-end from the LAST channel tag to EVERY pending ' +
      'marker across all sources: when ON, the ghost-reply Stop hook enumerates all ' +
      'obligated-and-unanswered inbounds and returns a single {"decision":"block"} ' +
      'listing each owed conversation by inbound_id + source + safe key, so a ' +
      'multi-thread agent is blocked until it answers ALL of them (with WS2 it ' +
      'answers each by inbound_id, no coordinate risk). Requires channel-block-turn-end ' +
      'also ON (the hook self-gates on both). Marker-semantics-neutral: it changes WHEN ' +
      'the hook blocks, arms/clears no markers. The env var ' +
      'AGT_BLOCK_TURN_END_ALL_MARKERS_ENABLED is the operator/canary override the bash ' +
      'hook reads directly (materialized by the manager). Ships dark; canary per host ' +
      '(agt-aws-1, where the multi-thread symptom is chronic) before any fleet flip. ' +
      'OFF (default) = the single-marker last-tag behavior, unchanged.',
    flagType: 'boolean',
    defaultValue: false,
    envVar: 'AGT_BLOCK_TURN_END_ALL_MARKERS_ENABLED',
    // Broadens a turn-completion gate across every thread; flipping it beyond the
    // canary is a high-blast-radius change worth an explicit confirm.
    sensitive: true,
  },
  {
    key: 'ghost-reply-intent-classifier',
    description:
      'Intentional-non-reply suppression on the ghost-reply RECOVERY path (ENG-7096). The ' +
      'recovery-outbox consumer in the channel MCP only ever fires for turns where the agent ' +
      'ended with text but did NOT call the matching reply tool for that conversation - a set ' +
      'that includes DELIBERATE non-replies (e.g. the agent narrating that the humans in the ' +
      'thread are talking among themselves and it is staying out). When ON, the consumer asks a ' +
      'cheap model (the conversation-eval backend, AGT_CONV_EVAL_*) whether the recovered text ' +
      'is a real message for the user (deliver) or an internal not-replying note (suppress), ' +
      'biased to DELIVER and fail-open, so a classifier outage degrades to today\'s behaviour, ' +
      'never to a dropped real reply. Boolean gate; ships dark. When OFF the consumer posts every ' +
      'recovered reply exactly as today.',
    flagType: 'boolean',
    defaultValue: false,
    envVar: 'AGT_GHOST_REPLY_INTENT_CLASSIFIER_ENABLED',
  },
  {
    key: 'compaction-notice',
    description:
      'Compaction courtesy notice (ENG-7339): when a managed Claude Code session compacts its ' +
      'context, the persistent session pauses and stops replying for a stretch, which looks ' +
      'identical to a dead agent from the channel. When ON, the generated PreCompact hook finds ' +
      'the conversation the user is actively on (the last <channel ...> tag in the transcript) ' +
      'and drops a notice into the matching <channel>-notice-outbox; the channel MCP server ' +
      '(alive while the Claude process compacts) posts a short "reorganizing my memory, back ' +
      'shortly" line. The notice path never clears the pending-inbound marker, so the genuine ' +
      'reply the agent still owes after compaction is unaffected. No active channel tag ⇒ silent ' +
      '(idle agents never broadcast). Boolean gate; ships dark, canary per host before any fleet ' +
      'flip. When OFF the hook writes nothing and the consumer drops any stray notice unsent.',
    flagType: 'boolean',
    defaultValue: false,
    envVar: 'AGT_COMPACTION_NOTICE_ENABLED',
  },
  {
    key: 'channel-live-progress',
    description:
      'Live in-channel progress indicator (ENG-6567 Phase 2): while an agent is mid-task on a ' +
      'pending channel inbound, the channel server maintains a slimline Block Kit "⏳ working… ' +
      '(last: <step>)" context message on the thread, driven by a throttled PostToolUse ' +
      'heartbeat, and clears it the moment the final reply lands. Answers "is it still working ' +
      'or done?" without the agent having to narrate. Boolean gate; ships dark — canary per host ' +
      'before any fleet flip. When OFF the heartbeat is still written (cheap, local) but nothing ' +
      'is ever posted, so behaviour is exactly as today.',
    flagType: 'boolean',
    defaultValue: false,
    envVar: 'AGT_CHANNEL_PROGRESS_ENABLED',
  },
  {
    key: 'agent-restart-approval',
    description:
      'Route an agent\'s self-restart request (ENG-6373) through HITL approval to its Manager ' +
      'instead of the local-confirm + flag fast path. Idle requests auto-approve + notify; ' +
      'requests with active in-flight work page the Manager. Boolean gate; ships dark. When OFF ' +
      'the request_restart tool falls back to the legacy local-flag behaviour.',
    flagType: 'boolean',
    defaultValue: false,
    envVar: 'AGT_AGENT_RESTART_APPROVAL_ENABLED',
    // Enabling lets a (possibly prompt-injected) agent file Manager-facing
    // approval requests; flipping it is a capability change worth confirming.
    sensitive: true,
  },
  {
    key: 'approval-sod',
    description:
      'Separation-of-duties (SoD) guard for the HITL approval FSM (ENG-6459): ' +
      'off = disabled, shadow = resolve principals + audit conflicts only (no block), ' +
      'enforce = fail-closed refuse of a self-approval or an unresolvable approver. ' +
      'Compares approver vs requesting-human on a canonical organization_people.person_id. ' +
      'This is the org-wide DEFAULT; a per-(team, verb) approval_policies.sod_mode row overrides ' +
      'it (ENG-6678: relax low-risk verbs while keeping enforce on money verbs). ' +
      'Ships dark (off); soak in shadow before enforce.',
    flagType: 'enum',
    allowedValues: ['off', 'shadow', 'enforce'],
    defaultValue: 'off',
    envVar: 'AGT_APPROVAL_SOD_MODE',
    // Access-control gate: flipping to enforce can REFUSE approvals (incl. a
    // legitimate approver whose Slack id isn't linked to a person row), so it
    // must soak in shadow first and is worth confirming on mutation.
    sensitive: true,
  },
  {
    key: 'direct-chat-doorbell',
    description:
      'Direct-chat doorbell + pull-cursor delivery (ENG-5927, ADR-0020): the manager rings a ' +
      'content-free doorbell and the agent\'s in-session MCP pulls via the capped ' +
      '/host/direct-chat/poll claim, replacing send-keys/one-shot delivery. Boolean gate; ships dark.',
    flagType: 'boolean',
    // Declared safe value is `false` — keeps today's send-keys/one-shot rail.
    // The doorbell rail is wired but not yet resume-aware (PR-4) nor carrying the
    // consume signal (PR-3), so flip on per-host only during the PR-5 canary.
    defaultValue: false,
    envVar: 'AGT_DIRECT_CHAT_DOORBELL_ENABLED',
  },
  {
    key: 'kanban-doorbell',
    description:
      'Durable kanban-check delivery (ENG-6785, ADR-0020 T1): the manager enqueues the ' +
      'kanban "check your board" nudge as a direct_chat_messages notice (ENG-6381 kind) and ' +
      'rings the direct-chat doorbell, instead of firing it over unverified tmux send-keys ' +
      '(which always returned delivered=false post-ENG-6345, so every kanban inject logged ' +
      '"failed" and the board never progressed). Boolean gate; ships dark.',
    flagType: 'boolean',
    // Declared safe value is `false` — keeps today's send-keys kanban inject. The
    // durable rail reuses the direct-chat notice queue (the board state is already
    // durable, so this only needs reliable WAKE delivery); flip on per-host to
    // canary before fleet rollout.
    defaultValue: false,
    envVar: 'AGT_KANBAN_DOORBELL_ENABLED',
  },
  {
    key: 'direct-chat-stream-reply',
    description:
      'Server-side type-out for direct-chat replies (direct-chat UI redesign Phase 2, ' +
      'docs/design/direct-chat-ui-chat-sdk.md): the channel MCP posts a small anchor via ' +
      '/host/direct-chat/reply, then reveals the rest of the (already-complete) reply ' +
      'progressively through /host/direct-chat/update so the webapp bubble grows instead ' +
      'of appearing as a wall of text. Purely cosmetic (the persisted reply is identical ' +
      'either way). Boolean gate; ships dark.',
    flagType: 'boolean',
    // Declared safe value is `false`, keeping today's single-shot reply (the agent
    // posts the full reply once). The type-out is a no-restart-flippable polish
    // gate; flip on per-host to dogfood before fleet rollout.
    defaultValue: false,
    envVar: 'AGT_DIRECT_CHAT_STREAM_REPLY_ENABLED',
  },
  {
    key: 'direct-chat-drawer',
    description:
      'Direct Chat drawer with the vendored Vercel Chat SDK UI (direct-chat UI redesign ' +
      'Slice 3, docs/design/direct-chat-ui-chat-sdk.md, ENG-6704): a flag-gated right-side ' +
      'drawer with markdown rendering, an auto-growing composer, scroll anchoring and a ' +
      'streamed-reply cursor, wired to the existing relay + Realtime. When on, the agent ' +
      'detail page shows the drawer instead of the plain Direct Chat tab; when off the tab ' +
      'is unchanged. Ships dark.',
    flagType: 'boolean',
    // PUBLIC (ADR-0022 §2): a UI-rollout flag the browser reads via GET /flags to
    // decide whether to render the drawer entry point (mirrors projects-menu).
    // Declared safe value is `false`: the existing Direct Chat tab stays the
    // surface until the drawer is rolled out per org from the admin Feature Flags
    // page. No envVar: a browser-only UI gate has no host-side override.
    defaultValue: false,
    public: true,
  },
  {
    key: 'manager-failure-notify',
    description:
      'Route terminal integration-failure notifications to the agent\'s human manager ' +
      '(reports_to → preferred channel) instead of only the agent\'s own direct-chat ' +
      '(ENG-6334). Boolean gate; ships dark.',
    flagType: 'boolean',
    defaultValue: false,
    envVar: 'AGT_MANAGER_FAILURE_NOTIFY_ENABLED',
  },
  {
    key: 'model-api-error-reporting',
    description:
      'Manager reports model-API failures (529 overloaded, 429 rate-limit, 5xx, timeouts) ' +
      'from spawned-agent output and its own eval/memory backend calls to the AGT API, which ' +
      'persists them and opens a threshold alert for early warning of provider incidents ' +
      '(ENG-7363). Boolean gate; ships dark. Materialized to the host flags-cache.',
    flagType: 'boolean',
    defaultValue: false,
    envVar: 'AGT_MODEL_API_ERROR_REPORTING_ENABLED',
  },
  {
    key: 'wedge-transient-notice',
    description:
      'When a wedge-respawn was preceded by a transient LLM-API error (529/429/503/500 that ' +
      'exhausted its retries and wedged the turn), the manager writes the ENG-6058 give-up ' +
      'signal tagged reason=transient_overload so the channel sweeps post a friendly ' +
      '"I hit a brief overload — please resend" notice instead of leaving the user in silence ' +
      '(ENG-7360, extends ENG-6861 to the retry-exhaustion + wedge path). Boolean gate; ships ' +
      'dark — channel-visible copy soaks per host before going wide.',
    flagType: 'boolean',
    defaultValue: false,
    envVar: 'AGT_WEDGE_TRANSIENT_NOTICE_ENABLED',
  },
  {
    key: 'manager-review-notify',
    description:
      'Deliver the agent\'s weekly performance-review check-in to its human manager ' +
      '(reports_to, chain-walked → preferred channel) via the submit_performance_review ' +
      'tool (ENG-6513). Boolean gate; ships dark.',
    flagType: 'boolean',
    defaultValue: false,
    envVar: 'AGT_MANAGER_REVIEW_NOTIFY_ENABLED',
  },
  {
    key: 'human-task-assignment',
    description:
      'Allow an agent to assign a kanban task to a human teammate via the ' +
      'assign_kanban_to_human MCP tool / POST /host/kanban/assign-human (ENG-6665). ' +
      'Off = the endpoint soft-refuses, so no agent can create human-assigned cards. ' +
      'This is the per-org activation gate for the whole human-assignment feature; ' +
      'the separate human-task-assignment-notify flag controls whether the recipient ' +
      'is also notified. Boolean gate; ships dark.',
    flagType: 'boolean',
    defaultValue: false,
  },
  {
    key: 'human-task-assignment-notify',
    description:
      'Notify a human teammate, via their preferred channel (Slack DM, else email ' +
      'fallback), when an agent assigns them a kanban task (ENG-6665, ' +
      '/host/kanban/assign-human). Off = the card is created silently (it still ' +
      'appears on the recipient\'s My Tasks page). Boolean gate; ships dark.',
    flagType: 'boolean',
    defaultValue: false,
  },
  {
    key: 'resume-reconciler',
    description:
      'Health-gated safe auto-resume for circuit-breaker pauses (ENG-6383, epic ENG-6333). ' +
      'When on, the manager clears a trip only when the dependency has genuinely recovered ' +
      '(status active + MCP present + connectivity ok ≥ hysteresis), replacing the blind ' +
      'ENG-6088 quiet-timer; when off, the blind timer still governs. Boolean gate; ships dark.',
    flagType: 'boolean',
    // Declared safe value is `false` — the blind ENG-6088 timer (already bounded
    // to ≤1 auto-resume/window) stays the behaviour when the API can't be
    // reached or no flag map arrives. Flip on per-host for the canary bake.
    defaultValue: false,
    // Migration override (ADR-0022): the pre-flags env gate stays the
    // highest-precedence operator override so a host already running with
    // AGT_RESUME_RECONCILER_ENABLED set keeps that value until the env is retired.
    envVar: 'AGT_RESUME_RECONCILER_ENABLED',
  },
  {
    key: 'memory-extraction',
    description:
      'Host-side durable-memory extraction (ENG-6200): the manager extracts ' +
      'candidate memories from completed conversations and POSTs them to ' +
      '/host/memories/candidates, which writes dream_log via promoteCandidates. ' +
      'Post persistent-session cutover this is the ONLY live consolidation path. ' +
      'Boolean gate; ships dark — when off the manager runs no extraction, so 0 ' +
      'dream_log rows fleet-wide is the expected steady state until it is armed.',
    flagType: 'boolean',
    // Declared safe value is `false` (no extraction). Fail-safe direction: a
    // flag-DB read error must never start sending candidate memories across the
    // host→control-plane boundary on its own. Flip on per-host for the canary,
    // then fleet-wide from the admin Feature Flags page.
    defaultValue: false,
    // Migration override (ADR-0022): the pre-flags env gate stays the
    // highest-precedence operator override so a host already running with
    // AGT_MEMORY_EXTRACTION_ENABLED set keeps that value until the env is retired.
    envVar: 'AGT_MEMORY_EXTRACTION_ENABLED',
  },
  {
    key: 'conversation-eval-backend',
    description:
      'Backend for host-side conversation-success scoring AND memory extraction (ENG-6581), ' +
      'which share one scorer: anthropic-api = Haiku via a direct Anthropic Messages API ' +
      'fetch (no subprocess); claude-p = Haiku via the `claude -p` subprocess (reuses the ' +
      'agent auth, effectively free under Max but a heavy spawn); local = an ' +
      'OpenAI-compatible loopback endpoint (transcript never leaves the host). Auth for the ' +
      'anthropic-api path comes from AGT_CONV_EVAL_ANTHROPIC_API_KEY (falls back to ' +
      'ANTHROPIC_API_KEY); with no key that path fails closed (eval disabled) rather than ' +
      'reverting to claude-p. Enum.',
    flagType: 'enum',
    allowedValues: ['anthropic-api', 'claude-p', 'local'],
    // Declared safe value is `claude-p` - it matches the manager's compiled
    // default before this flag existed, so migrating the host reader onto the
    // flag (ADR-0022) preserves fleet behaviour rather than silently moving eval
    // onto the metered direct API (which would also fail closed on any host that
    // lacks an Anthropic API key). The flip to `anthropic-api` is a deliberate,
    // staged cost decision from the admin Feature Flags page (claude-p is
    // deprecating under Max, ENG-5576).
    defaultValue: 'claude-p',
    // Migration override (ADR-0022): the pre-flags env var stays the
    // highest-precedence operator override so a host already setting
    // AGT_CONV_EVAL_BACKEND keeps that value until the env var is retired.
    envVar: 'AGT_CONV_EVAL_BACKEND',
  },
  {
    key: 'synthetic-probe-on-metered-hosts',
    description:
      'Synthetic liveness probing of OpenRouter-metered agents (ENG-7235). On a host whose ' +
      "claude_auth_mode='openrouter' every model call is per-token cost, so the hourly synthetic " +
      'probe (a full inbound→session→model→outbound round-trip whose only output is a probe_ack) ' +
      'is real spend for zero deliverable - unlike a Max-subscription host where it is effectively ' +
      'free. When OFF (default) the synthetic-probe cron skips OpenRouter-mode agents entirely; ' +
      'their liveness falls back to the zero-token signals (pane-activity ENG-5399 + manager ' +
      'heartbeat + inbound-loop liveness ENG-5614). When ON, metered agents are probed like every ' +
      'other agent (the pre-ENG-7235 behaviour) - the explicit opt-in for an operator who wants ' +
      'the end-to-end check and accepts the cost. Subscription / api_key hosts are unaffected ' +
      'either way. Evaluated as a global stage value by the central cron (no per-org targeting in ' +
      'v1). Boolean gate.',
    flagType: 'boolean',
    // Declared safe value is `false` = do NOT probe metered agents. This both
    // encodes the ENG-7235 intent (no per-token cost for a no-deliverable probe)
    // and is the fail-safe direction for the cron's flag read: a flag-DB error
    // degrades to the cost-avoiding default rather than silently re-arming paid
    // probes fleet-wide. No envVar - net-new control with no pre-flags env gate
    // to migrate (ADR-0022).
    defaultValue: false,
  },
  {
    key: 'skill-dreaming',
    description:
      'Memory-driven skill improvement (ENG-6500): the nightly dreaming cron analyzes each ' +
      "agent's promoted agent_memories against its in-scope skills (agent/team/org/global) and " +
      'auto-drafts concrete skill improvements as status=draft skill_definitions. Drafts carry ' +
      'their evidence memories + rationale + confidence as provenance and flow through the ' +
      'existing scan → Pending-Skills review → publish funnel — never auto-published. Boolean ' +
      'gate; ships dark — when off the cron drafts nothing, so 0 skill-dreaming drafts is the ' +
      'expected steady state until an org is armed from the admin Feature Flags page.',
    flagType: 'boolean',
    // Declared safe value is `false` (no drafting). Fail-safe direction: a
    // flag-DB read error must never start writing draft skills on its own. No
    // envVar — this is a net-new control with no pre-flags env gate to migrate.
    defaultValue: false,
  },
  {
    key: 'skill-draft-review-notify',
    description:
      'Pending skill-draft review nudge (ENG-6505): when a skill lands as a status=draft ' +
      'awaiting operator review, send a low-severity informational notification to the right ' +
      "reviewer(s) by scope — agent-scoped → the agent's manager (reports_to_person), falling " +
      'back to team owners/admins; team-scoped → team owners/admins; org-scoped → org ' +
      'owners/admins — with a deep link to the Pending Skills card. Distinct from (and never ' +
      'doubles up on) the HIGH+ SkillSpector security alert. Covers both the agent-authored ' +
      '(ENG-4589) and skill-dreaming (ENG-6500) draft paths. Boolean gate; ships dark — when ' +
      'off no nudge is sent, so the steady state until an org is armed is silence.',
    flagType: 'boolean',
    // Declared safe value is `false` (no nudge). Fail-safe direction: a flag-DB
    // read error must never start DMing humans on its own. No envVar — net-new
    // control with no pre-flags env gate to migrate (ADR-0022).
    defaultValue: false,
  },
  {
    key: 'skill-fragments',
    description:
      'Agent-contributed additive skill fragments (ENG-6811, epic ENG-6805 P2a): when on, ' +
      'an agent can propose an additive fragment for a shared (team/org) skill via the ' +
      'skill_contribute_fragment MCP tool; accepted fragments compose into the parent ' +
      "skill's delivered body in one delimited region at /host/refresh. This is the per-org " +
      'activation gate for the whole additive path - it gates BOTH the contribute tool ' +
      '(off = soft-refuse, no fragment is created) AND compose-at-refresh (off = agents ' +
      'receive the core body only), so flipping it off is a clean kill switch that reverts ' +
      'shared skills to operator-owned content. Boolean gate; ships dark.',
    flagType: 'boolean',
    // Declared safe value is `false` (no contribution, no compose). Fail-safe
    // direction: a flag-DB read error must never start composing agent-authored
    // content into a shared skill's delivered body on its own. No envVar - net-new
    // control with no pre-flags env gate to migrate (ADR-0022).
    defaultValue: false,
  },
  {
    key: 'skill-revision-proposals',
    description:
      'Agent-proposed corrective rewrites of shared skills (ENG-6824, epic ENG-6805 P2b): ' +
      'when on, an agent can propose a full-body rewrite of a shared (team/org) skill via the ' +
      'skill_propose_revision MCP tool, anchored on an immutable base_version_id; an operator ' +
      'reviews a machine-derived diff and approves (conflict-guarded apply) or rejects. This is ' +
      'the per-org activation gate for the corrective path - off = the propose tool soft-refuses, ' +
      'so no proposal is created. Distinct from skill-fragments (the additive path). Approved ' +
      'revisions land via the existing operator-owned skill body + delivery, so there is nothing ' +
      'to revert when flipped off. Boolean gate; ships dark.',
    flagType: 'boolean',
    // Declared safe value is `false` (no proposals). Fail-safe direction: a
    // flag-DB read error must never let an agent file rewrites of operator-owned
    // shared skills on its own. No envVar - net-new control (ADR-0022).
    defaultValue: false,
  },
  {
    key: 'skills-marketplace',
    description:
      'Cross-org skills marketplace: an org publishes a skill (agent proposes via ' +
      'skill_publish_to_marketplace, org owner/admin approves) so agents in OTHER orgs can ' +
      'browse the listing and import a copy into their own draft -> SkillSpector scan -> ' +
      'Pending-review -> publish funnel. Per-org activation gate for the whole feature: it ' +
      'gates the propose tool + the /marketplace publish/browse/import routes + the webapp ' +
      'marketplace UI. Off = propose/import soft-refuse and browse returns nothing, so no ' +
      'org content crosses an org boundary. Boolean gate; ships dark - flip on per org from ' +
      'the admin Feature Flags page.',
    flagType: 'boolean',
    // PUBLIC (ADR-0022 §2): the webapp reads it via GET /flags to decide whether to render
    // the Marketplace panels (mirrors projects-menu / direct-chat-drawer). Declared safe
    // value is `false`: publishing a skill to other orgs is outward-facing IP exposure, so
    // the fail-safe / absent-flag direction is "nothing is published or importable". No
    // envVar - net-new control with no pre-flags env gate to migrate.
    defaultValue: false,
    public: true,
  },
  {
    key: 'channel-quarantine-mode',
    description:
      'Optional-channel quarantine (ENG-5932): off = disabled, shadow = log matches only, enforce = quarantine.',
    flagType: 'enum',
    allowedValues: ['off', 'shadow', 'enforce'],
    // Declared safe value is `shadow` (compute + log "would quarantine X",
    // takes no action) — NOT `off`. Two reasons: (1) shadow is observe-only, so
    // it's as non-destructive as off while staying diagnosable; (2) it matches
    // the live manager's compiled default since ENG-5932, so migrating the host
    // reader onto this flag (ENG-6252) preserves fleet behaviour rather than
    // silently disabling the quarantine logic. Enforcement (`enforce`) stays a
    // deliberate, audited flip via the admin surface.
    defaultValue: 'shadow',
    envVar: 'AGT_CHANNEL_QUARANTINE_MODE',
    // Enforcement gate: weakening it (enforce → shadow/off) drops a channel
    // safety control, so mutations require explicit confirmation.
    sensitive: true,
  },
  {
    key: 'slack-reply-binding',
    description:
      'Slack reply-target binding (ENG-7396 / WS2, cross-thread reply routing): ' +
      'the model names which inbound it answers (inbound_id) and the server ' +
      'resolves the destination from its own verified record. shadow = classify + ' +
      'count only, routing unchanged; warn = same routing plus a tool-result note ' +
      'so the model self-corrects; enforce = route a bound reply to the registry ' +
      'thread and REJECT a reply whose target matches no inbound this process ' +
      'delivered. The channel server can\'t evaluate flags, so the manager ' +
      'materializes the resolved value into the AGT_SLACK_REPLY_BINDING spawn env ' +
      '(the operator/canary override, ADR-0022). Ships shadow.',
    flagType: 'enum',
    allowedValues: ['shadow', 'warn', 'enforce'],
    // Declared safe value is `shadow`: observe + count only, no behaviour change.
    // enforce is the deliberate, audited flip that actually blocks/reroutes.
    defaultValue: 'shadow',
    envVar: 'AGT_SLACK_REPLY_BINDING',
    // enforce blocks/reroutes egress, so weakening or flipping it is worth an
    // explicit confirm (ADR-0022 §4).
    sensitive: true,
  },
  {
    key: 'workflows-down-sync',
    description:
      'Down-sync active dynamic workflows to agents via /host/refresh (ADR-0012, ENG-6352). When on, the API populates the per-agent workflow set (team default ∪ agent override) and the claude-code adapter writes each as .claude/workflows/<name>.js. Boolean gate; ships dark.',
    flagType: 'boolean',
    // Declared safe value is `false` (no down-sync). The fail-safe direction is
    // dark: if the API can't reach the flag DB, agents simply receive no
    // workflows — never a partial or stale set written to their project dir.
    // Per-org rollout: flip on for one org from the admin Feature Flags page.
    defaultValue: false,
    // Migration override (ADR-0022): the pre-flags env gate stays the
    // highest-precedence operator override so existing AGT_WORKFLOWS_ENABLED
    // hosts/stages keep working until the env var is retired.
    envVar: 'AGT_WORKFLOWS_ENABLED',
  },
  {
    key: 'integration-connectivity-escalation',
    description:
      'Arm integration-connectivity escalation (ENG-5641). When on, the connectivity-monitor cron opens/closes integration_down alerts; when off it stays shadow — computes and logs what it WOULD page but opens nothing. Boolean gate; ships dark.',
    flagType: 'boolean',
    // Declared safe value is `false` (shadow / no paging). It matches the cron's
    // compiled default since ENG-5641, so migrating the reader onto this flag
    // (ADR-0022) preserves fleet behaviour: escalation stays dark until an
    // operator deliberately arms it per-stage from the admin Feature Flags page.
    // `false` is also the fail-safe direction — a flag-DB read error must never
    // start paging on its own.
    defaultValue: false,
    // Migration override (ADR-0022): the pre-flags env gate stays the
    // highest-precedence operator override so existing
    // AUGMENTED_CONNECTIVITY_ESCALATION_ENABLED stages keep working until the
    // env var is retired.
    envVar: 'AUGMENTED_CONNECTIVITY_ESCALATION_ENABLED',
  },
  {
    key: 'projects-menu',
    description:
      'Show the Projects nav item in the webapp (ADR-0017 Projects soft launch, ENG-6342). Replaces the isAdmin/adminOnly gate with a per-org rollout flag.',
    flagType: 'boolean',
    // PUBLIC (ADR-0022 §2): this is a UI-rollout flag the browser legitimately
    // needs, so it's the one flag serialized to the client via GET /flags.
    // Evaluated per the caller's active org — flip it on per-org from the admin
    // Feature Flags page to reveal Projects for that org. No env override (UI
    // flag, not an operator gate). Ships dark (default false).
    public: true,
    defaultValue: false,
  },
  {
    key: 'platform-maintenance-mode',
    description:
      'Platform-wide maintenance mode (ENG-6506): when on, every agent across every ' +
      'team/org auto-replies to admitted human inbound with a "we are offline for ' +
      'maintenance" notice instead of dispatching to the agent — for whole-platform ' +
      'downtime such as DB upgrades. Read host-side per inbound from the flags cache ' +
      '(no MCP restart). Boolean gate; ships dark — when off, channels behave exactly ' +
      'as today. (Proactive "back online" follow-up is the ENG-6508 fast-follow, not ' +
      'this flag.)',
    flagType: 'boolean',
    // Declared safe value is `false` (normal operation). Fail-safe direction: a
    // flag-DB read error or absent flag map must never silence the fleet on its
    // own — agents stay responsive unless an operator deliberately flips this on
    // from the admin Feature Flags page. No envVar: net-new control, no
    // pre-flags env gate to migrate.
    defaultValue: false,
    // Flipping this takes the ENTIRE fleet offline to end users; a mis-flip is a
    // platform-wide availability incident, so mutations require explicit
    // confirmation (the admin flip-reach modal).
    sensitive: true,
  },
  {
    key: 'human-hours-given-back',
    description:
      'Show the Human Hours Given Back value metric on the agent productivity surface ' +
      '(ENG-6750): an acceptance-anchored estimate of the human time an agent gave back ' +
      '(kanban tasks completed and not reverted + end-user conversations the success-eval ' +
      'rated helpful, quality-weighted, priced by a versioned rate table). Augmentation ' +
      'framing, not a "does the work of N people" substitution claim. Ships dark; flip on ' +
      'per org from the admin Feature Flags page.',
    flagType: 'boolean',
    // PUBLIC (ADR-0022 §2): a UI-rollout flag the browser reads via GET /flags to
    // decide whether to render the metric block (mirrors projects-menu /
    // direct-chat-drawer). Declared safe value is `false`: the block stays hidden
    // until rolled out per org. No envVar — a browser-only UI gate has no
    // host-side override.
    defaultValue: false,
    public: true,
  },
  {
    key: 'cross-team-kanban-assign',
    description:
      'Allow an agent to assign a kanban task to an agent on a DIFFERENT team in ' +
      'the same organization (ENG-6906), reusing the cross-team peer-messaging ' +
      'consent model: an org set to `unrestricted` needs no grant, an org set to ' +
      '`consent_required` needs a live cross_team_peer_grant. Off = the assign ' +
      'route refuses any cross-team target (same-team kanban_assign is unaffected), ' +
      'so no agent can place a card on another team. Per-org activation gate; ' +
      'ships dark.',
    flagType: 'boolean',
    // Declared safe value is `false`: cross-team assignment is a net-new
    // capability that crosses a team trust boundary, so the fail-safe / absent-
    // flag direction is "no cross-team assignment". No envVar — net-new control
    // with no pre-flags env gate to migrate.
    defaultValue: false,
  },
  {
    key: 'pmf-survey-dispatch',
    description:
      'Fortnightly PMF survey dispatch cron (ENG-6936 / ENG-6958). When on, the ' +
      'daily pmf-survey-dispatcher selects each agent reports_to person due on ' +
      'their 14-day anniversary (anchored to their earliest reporting agent) and ' +
      'records a pending dispatch row for the email + in-app senders to fulfil. ' +
      'Global gate; ships dark. The DB stage value is the kill switch (no env ' +
      'override). Off = the cron does nothing, so no survey is ever dispatched.',
    flagType: 'boolean',
    // Declared safe value is `false`: this drives outbound surveys to customer
    // contacts, so the absent-flag / fail-safe direction is "send nothing".
    defaultValue: false,
  },
  {
    key: 'augmented-support-writes',
    description:
      'Self-remediation writes on the augmented-support host surface (ENG-7000, ADR-0032 ' +
      'Phase 2b). When on, the org-locked /host/support/* write routes (create_agent, ' +
      'modify_agent, install_integration) are reachable; they call the shared agent ' +
      'write-core scoped to the verified host org. Ships dark: the server-rendered HITL ' +
      'approval gate is ENG-7001 (Phase 2c), so until that lands and provisioning attaches ' +
      'the write scope, this flag stays OFF and the write routes return feature_disabled. ' +
      'The DB stage/org override is the control (no env override).',
    flagType: 'boolean',
    // Declared safe value is `false`: these are WRITES against customer infrastructure
    // (creating/modifying agents in the caller's org). The fail-safe / absent-flag
    // direction is "no self-remediation write is possible".
    defaultValue: false,
    // Customer-infrastructure write capability; flipping it on is worth an explicit
    // confirm (ADR-0022 §4), same posture as admin-send-keys.
    sensitive: true,
  },
  {
    key: 'augmented-support-auto-provision',
    description:
      'Org-create auto-provision of the per-org system_support concierge agent ' +
      '("Sherlock") (ENG-7026, ADR-0032 §7). This is the FINAL rollout ' +
      'stage: when on, every newly created organization gets a support agent ' +
      'provisioned (in draft - arming still needs first-run consent) by a ' +
      'best-effort hook in the org-create handler. The earlier rollout stages ' +
      '(dogfood Integrity Labs, then design partners) are driven by the explicit ' +
      'admin command (provision-support --org <slug>), NOT this flag. Ships dark: ' +
      'default OFF means the hook is inert, so there is no fleet-wide first deploy ' +
      '- existing orgs are never touched and new orgs get nothing until this is ' +
      'flipped on. The DB stage value is the control (no env override).',
    flagType: 'boolean',
    // Declared safe value is `false`: the fail-safe / absent-flag direction is
    // "no org auto-provisions a support agent", so a misread can never silently
    // create agents across the fleet.
    defaultValue: false,
    // Auto-creates an org-admin-equivalent agent in every new org; flipping it on
    // is fleet-shaping and worth an explicit confirm (ADR-0022 §4).
    sensitive: true,
  },
  {
    key: 'kanban-source-ambiguity-guard',
    description:
      'Kanban source ambiguity guard (ENG-7394 / WS1a, Slack cross-thread reply ' +
      'routing). The kanban add path auto-injects source_integration / ' +
      'source_external_id from the recent-inbound cache when the agent omits them. ' +
      'When ON for an org, that fallback only fires when EXACTLY ONE fresh inbound ' +
      'thread exists (read from agent_recent_inbound_sources within the freshness ' +
      'window); two or more live threads inject nothing instead of the most-recent ' +
      "thread, so a concurrent second user's card can't be attributed to the wrong " +
      'thread. Off (default) = legacy single-slot last-inbound-wins. Evaluated ' +
      'per-org in POST /host/kanban via getEvaluatedFlags(); rollout is a per-org ' +
      'override (no env override - this is an API-route gate, like admin-live-pane). ' +
      'Ships dark.',
    flagType: 'boolean',
    // Declared safe value is `false`: the fail-safe / absent-flag direction is the
    // current behaviour (single-slot injection). Turning it ON only makes the
    // fallback MORE conservative (injects less), so it removes no control.
    defaultValue: false,
  },
] as const;

const REGISTRY_BY_KEY: ReadonlyMap<string, FlagDefinition> = new Map(
  FLAG_REGISTRY.map((definition) => [definition.key, definition]),
);

export function getFlagDefinition(key: string): FlagDefinition | undefined {
  return REGISTRY_BY_KEY.get(key);
}

export function listFlagDefinitions(): readonly FlagDefinition[] {
  return FLAG_REGISTRY;
}

/**
 * Keys of flags safe to serialize to a browser client (ADR-0022 §2). Only
 * `public: true` flags qualify; the `GET /flags` endpoint filters its
 * evaluated map to this set so private/backend gates never reach the client.
 */
export function listPublicFlagKeys(): readonly string[] {
  return FLAG_REGISTRY.filter((definition) => definition.public === true).map(
    (definition) => definition.key,
  );
}

/** Is this flag a sensitive / enforcement gate (mutation needs confirmation)? */
export function isSensitiveFlag(key: string): boolean {
  return REGISTRY_BY_KEY.get(key)?.sensitive === true;
}
